import { useCallback, useRef } from 'react';

interface TapHandlerOptions {
  onTap?: () => void;
  onLongPress?: () => void;
  /** Long-press duration in ms. Defaults to 500. */
  longPressMs?: number;
  /** Movement threshold in pixels above which the gesture is treated as a scroll. Defaults to 10. */
  moveThreshold?: number;
}

interface TapHandlers {
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchMove: (e: React.TouchEvent) => void;
  onTouchEnd: (e: React.TouchEvent) => void;
  onTouchCancel: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
  onClick: (e: React.MouseEvent) => void;
}

/**
 * Touch handler that distinguishes tap from scroll.
 *
 * Bug fix: previously onTouchEnd fired onTap unconditionally, so any scroll
 * gesture that started on a row would trigger its tap action. Now we track
 * the touch start position; if the finger moves beyond `moveThreshold` we
 * cancel both the long-press timer and suppress the tap.
 */
export function useTapHandler({
  onTap,
  onLongPress,
  longPressMs = 500,
  moveThreshold = 10,
}: TapHandlerOptions): TapHandlers {
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const didLongPress = useRef(false);
  const didMove = useRef(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const isTouch = useRef(false);

  const cancelLongPress = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      isTouch.current = true;
      didLongPress.current = false;
      didMove.current = false;
      const touch = e.touches[0];
      startX.current = touch.clientX;
      startY.current = touch.clientY;

      if (onLongPress) {
        longPressTimer.current = setTimeout(() => {
          if (!didMove.current) {
            didLongPress.current = true;
            onLongPress();
          }
        }, longPressMs);
      }
    },
    [onLongPress, longPressMs],
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (didMove.current) return;
      const touch = e.touches[0];
      const dx = touch.clientX - startX.current;
      const dy = touch.clientY - startY.current;
      if (Math.abs(dx) > moveThreshold || Math.abs(dy) > moveThreshold) {
        didMove.current = true;
        cancelLongPress();
      }
    },
    [moveThreshold, cancelLongPress],
  );

  const handleTouchEnd = useCallback(() => {
    cancelLongPress();
    if (!didLongPress.current && !didMove.current && onTap) {
      onTap();
    }
  }, [onTap, cancelLongPress]);

  const handleTouchCancel = useCallback(() => {
    cancelLongPress();
    didMove.current = true; // suppress any pending tap
  }, [cancelLongPress]);

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (onLongPress) onLongPress();
    },
    [onLongPress],
  );

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      // Touch devices already handled via touch events. Only fire for genuine
      // mouse clicks on devices without touch.
      if (isTouch.current) return;
      if (e.detail > 0 && onTap) onTap();
    },
    [onTap],
  );

  return {
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
    onTouchCancel: handleTouchCancel,
    onContextMenu: handleContextMenu,
    onClick: handleClick,
  };
}
